/**
 * Trang thai phien nguoi choi.
 *
 * Giu MOT nguon su that ve "dang dang nhap hay khong" cho toan bo ung dung.
 * Khi may chu tra ve 401, phien bi xoa o day va moi man hinh tu dong ve dang nhap —
 * khong de mot man hinh nao con hien so du cu cua phien da het han.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type JSX,
  type ReactNode,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, getToken, setToken, setUnauthorisedHandler, type Me, type WalletRow } from './api';

interface SessionValue {
  me: Me | null;
  wallets: WalletRow[];
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (identifier: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => void;
}

const SessionContext = createContext<SessionValue | null>(null);

/**
 * Dau van tay thiet bi.
 *
 * May chu dung no de canh bao dang nhap tu thiet bi la (PLR-R21) va de phat hien
 * nhom da tai khoan (PLR-R04). Day la ban toi thieu, on dinh trong pham vi mot
 * trinh duyet; ban that nen dung thu vien chuyen dung.
 */
function deviceFingerprint(): string {
  const KEY = 'gikidi.device';
  try {
    const existing = localStorage.getItem(KEY);
    if (existing) return existing;
    const generated =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `dev-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(KEY, generated);
    return generated;
  } catch {
    return 'dev-khong-luu-duoc';
  }
}

export function SessionProvider({ children }: { children: ReactNode }): JSX.Element {
  const queryClient = useQueryClient();
  const [hasToken, setHasToken] = useState<boolean>(() => Boolean(getToken()));

  // Phien het han o bat ky loi goi nao deu dan ve mot cho duy nhat.
  useEffect(() => {
    setUnauthorisedHandler(() => {
      setToken(null);
      setHasToken(false);
      queryClient.clear();
    });
  }, [queryClient]);

  const meQuery = useQuery({
    queryKey: ['me'],
    queryFn: api.me,
    enabled: hasToken,
    retry: false,
    staleTime: 30_000,
  });

  const walletQuery = useQuery({
    queryKey: ['wallet'],
    queryFn: api.wallet.list,
    enabled: hasToken && Boolean(meQuery.data),
    // So du phai tuoi: nguoi choi vua dat cuoc xong la muon thay ngay.
    staleTime: 5_000,
    refetchInterval: 30_000,
  });

  const login = useCallback(
    async (identifier: string, password: string) => {
      const session = await api.auth.login(identifier, password, deviceFingerprint());
      setToken(session.access_token);
      setHasToken(true);
      await queryClient.invalidateQueries();
    },
    [queryClient],
  );

  const logout = useCallback(async () => {
    try {
      await api.auth.logout();
    } catch {
      // Ma truy cap co the da het han; van xoa phien o phia giao dien.
    }
    setToken(null);
    setHasToken(false);
    queryClient.clear();
  }, [queryClient]);

  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['wallet'] });
  }, [queryClient]);

  const value = useMemo<SessionValue>(
    () => ({
      me: meQuery.data ?? null,
      wallets: walletQuery.data?.wallets ?? [],
      isLoading: hasToken && meQuery.isLoading,
      isAuthenticated: hasToken && Boolean(meQuery.data),
      login,
      logout,
      refresh,
    }),
    [hasToken, meQuery.data, meQuery.isLoading, walletQuery.data, login, logout, refresh],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSession phai dung ben trong SessionProvider');
  return value;
}

/** Vi theo tien te tai khoan. Nguoi choi chi co mot tien te sau giao dich dau tien. */
export function usePrimaryWallet(): WalletRow | null {
  const { me, wallets } = useSession();
  if (!me) return null;
  return wallets.find((w) => w.currency === me.currency) ?? wallets[0] ?? null;
}

export { deviceFingerprint };
